/**
 * Modal de création d'une nouvelle worksheet
 */

import { useState } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Input } from '../ui/Input';
import { Textarea } from '../ui/Textarea';
import { Select } from '../ui/Select';
import {
  Workstation,
  WorksheetType,
  ActionType,
  CreateWorksheetDto,
} from '../../types/worksheets.types';
import { createWorksheet } from '../../services/worksheets.api';

const TYPE_OPTIONS: { value: WorksheetType; label: string }[] = [
  { value: 'OPENING', label: 'Ouverture' },
  { value: 'CLOSING', label: 'Fermeture' },
  { value: 'RECEPTION', label: 'Réception marchandise' },
  { value: 'PREPARATION', label: 'Préparation commandes' },
  { value: 'INVENTORY', label: 'Inventaire' },
  { value: 'MAINTENANCE', label: 'Maintenance' },
  { value: 'CUSTOM', label: 'Personnalisé' },
];

const ACTION_TYPE_OPTIONS: { value: ActionType; label: string }[] = [
  { value: 'CHECK', label: 'Vérification (oui/non)' },
  { value: 'VALIDATE', label: 'Validation' },
  { value: 'COUNT', label: 'Comptage' },
  { value: 'MOVE', label: 'Déplacement' },
  { value: 'PRINT', label: 'Impression' },
  { value: 'SCAN', label: 'Scan code-barres' },
  { value: 'WEIGH', label: 'Pesée' },
  { value: 'PHOTO', label: 'Photo' },
  { value: 'SIGN', label: 'Signature' },
  { value: 'FREE_TEXT', label: 'Texte libre' },
];

interface TaskInput {
  id: string;
  title: string;
  instructions: string;
  action_type: ActionType;
}

interface WorksheetCreateModalProps {
  workstations: Workstation[];
  onClose: () => void;
}

export default function WorksheetCreateModal({
  workstations,
  onClose,
}: WorksheetCreateModalProps) {
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [workstationId, setWorkstationId] = useState('');
  const [type, setType] = useState<WorksheetType>('CUSTOM');
  const [tasks, setTasks] = useState<TaskInput[]>([
    { id: crypto.randomUUID(), title: '', instructions: '', action_type: 'CHECK' },
  ]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // Add task
  const addTask = () => {
    setTasks([
      ...tasks,
      { id: crypto.randomUUID(), title: '', instructions: '', action_type: 'CHECK' },
    ]);
  };

  // Remove task
  const removeTask = (id: string) => {
    if (tasks.length <= 1) return;
    setTasks(tasks.filter((t) => t.id !== id));
  };

  // Update task
  const updateTask = (id: string, field: keyof TaskInput, value: string) => {
    setTasks(
      tasks.map((t) =>
        t.id === id ? { ...t, [field]: value } : t
      )
    );
  };

  // Move task up/down
  const moveTask = (id: string, direction: 'up' | 'down') => {
    const index = tasks.findIndex((t) => t.id === id);
    if (
      (direction === 'up' && index === 0) ||
      (direction === 'down' && index === tasks.length - 1)
    ) {
      return;
    }

    const newTasks = [...tasks];
    const swapIndex = direction === 'up' ? index - 1 : index + 1;
    [newTasks[index], newTasks[swapIndex]] = [newTasks[swapIndex], newTasks[index]];
    setTasks(newTasks);
  };

  // Validate and submit
  const handleSubmit = async () => {
    // Validation
    if (!title.trim()) {
      setError('Le titre est requis');
      return;
    }
    if (!workstationId) {
      setError('Veuillez sélectionner un poste de travail');
      return;
    }
    if (tasks.some((t) => !t.title.trim())) {
      setError('Toutes les tâches doivent avoir un titre');
      return;
    }

    try {
      setLoading(true);
      setError(null);

      const data: CreateWorksheetDto = {
        workstation_id: workstationId,
        type,
        title: title.trim(),
        description: description.trim() || undefined,
        tasks: tasks.map((t, index) => ({
          order: index + 1,
          title: t.title.trim(),
          instructions: t.instructions.trim() || undefined,
          action_type: t.action_type,
        })),
      };

      await createWorksheet(data);
      onClose();
    } catch (err: any) {
      console.error('Create failed:', err);
      setError(err.response?.data?.message || 'Erreur lors de la création');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Modal
      isOpen={true}
      onClose={onClose}
      title="Nouvelle Worksheet"
      size="lg"
      footer={
        <>
          <Button variant="outline" onClick={onClose} disabled={loading}>
            Annuler
          </Button>
          <Button onClick={handleSubmit} disabled={loading}>
            {loading ? 'Création...' : 'Créer la worksheet'}
          </Button>
        </>
      }
    >
      <div className="space-y-6">
        {/* Basic info */}
        <div className="grid grid-cols-2 gap-4">
          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Titre *
            </label>
            <Input
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Ex: Ouverture matin 01/12"
              className="w-full"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Poste de travail *
            </label>
            <Select
              value={workstationId}
              onChange={(e) => setWorkstationId(e.target.value)}
              className="w-full"
            >
              <option value="">Sélectionner...</option>
              {workstations.map((ws) => (
                <option key={ws.id} value={ws.id}>
                  {ws.name}
                </option>
              ))}
            </Select>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Type
            </label>
            <Select
              value={type}
              onChange={(e) => setType(e.target.value as WorksheetType)}
              className="w-full"
            >
              {TYPE_OPTIONS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </Select>
          </div>

          <div className="col-span-2">
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Description
            </label>
            <Textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Description optionnelle..."
              rows={2}
              className="w-full"
            />
          </div>
        </div>

        {/* Tasks */}
        <div>
          <div className="flex items-center justify-between mb-3">
            <label className="block text-sm font-medium text-gray-700">
              Tâches ({tasks.length})
            </label>
            <Button variant="outline" size="sm" onClick={addTask}>
              + Ajouter une tâche
            </Button>
          </div>

          <div className="space-y-3 max-h-64 overflow-y-auto">
            {tasks.map((task, index) => (
              <div
                key={task.id}
                className="border border-gray-200 rounded-lg p-3 bg-gray-50"
              >
                <div className="flex items-start gap-3">
                  {/* Order */}
                  <div className="flex flex-col gap-1">
                    <button
                      type="button"
                      onClick={() => moveTask(task.id, 'up')}
                      disabled={index === 0}
                      className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 15l7-7 7 7" />
                      </svg>
                    </button>
                    <span className="text-sm font-medium text-gray-500 text-center">
                      {index + 1}
                    </span>
                    <button
                      type="button"
                      onClick={() => moveTask(task.id, 'down')}
                      disabled={index === tasks.length - 1}
                      className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-30"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </button>
                  </div>

                  {/* Task fields */}
                  <div className="flex-1 grid grid-cols-3 gap-2">
                    <Input
                      value={task.title}
                      onChange={(e) => updateTask(task.id, 'title', e.target.value)}
                      placeholder="Titre de la tâche *"
                      className="col-span-2"
                    />
                    <Select
                      value={task.action_type}
                      onChange={(e) =>
                        updateTask(task.id, 'action_type', e.target.value)
                      }
                    >
                      {ACTION_TYPE_OPTIONS.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </Select>
                    <Input
                      value={task.instructions}
                      onChange={(e) =>
                        updateTask(task.id, 'instructions', e.target.value)
                      }
                      placeholder="Instructions (optionnel)"
                      className="col-span-3"
                    />
                  </div>

                  {/* Remove button */}
                  <button
                    type="button"
                    onClick={() => removeTask(task.id)}
                    disabled={tasks.length <= 1}
                    className="p-1 text-red-400 hover:text-red-600 disabled:opacity-30"
                  >
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Error */}
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg text-sm">
            {error}
          </div>
        )}
      </div>
    </Modal>
  );
}
